import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Checkbox,
    Divider,
    Button,
    CircularProgress,
    TextField,
    InputAdornment
} from '@mui/material';
import {
    Search as SearchIcon,
    Save as SaveIcon,
    ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import roleAdminService from '../../../services/roleAdminService';
import permissionService from '../../../services/permissionService';
import { useSnackbar } from 'notistack';

const RolePermissions = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();

    const [role, setRole] = useState(null);
    const [allPermissions, setAllPermissions] = useState([]);
    const [selectedPermissions, setSelectedPermissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchData();
    }, [id]);

    const fetchData = async () => {
        setLoading(true);
        try {
            // Récupérer le rôle
            const roleData = await roleAdminService.getRole(id);
            setRole(roleData);

            // Récupérer toutes les permissions
            const permissions = await permissionService.getAllPermissions();
            setAllPermissions(permissions);

            // Déterminer les permissions sélectionnées
            const rolePermissionIds = roleData.directPermissions?.map(p => p.id) || [];
            setSelectedPermissions(rolePermissionIds);
        } catch (error) {
            enqueueSnackbar('Erreur lors du chargement des données', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleTogglePermission = (permissionId) => {
        setSelectedPermissions(prev => {
            if (prev.includes(permissionId)) {
                return prev.filter(id => id !== permissionId);
            } else {
                return [...prev, permissionId];
            }
        });
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            // Supprimer toutes les permissions actuelles
            const currentPermissions = role.directPermissions || [];
            for (const permission of currentPermissions) {
                await roleAdminService.revokePermissionFromRole(id, permission.id);
            }

            // Ajouter les permissions sélectionnées
            for (const permissionId of selectedPermissions) {
                await roleAdminService.assignPermissionToRole(id, permissionId);
            }

            enqueueSnackbar('Permissions mises à jour avec succès', { variant: 'success' });
            fetchData(); // Rafraîchir les données
        } catch (error) {
            enqueueSnackbar('Erreur lors de la mise à jour des permissions', { variant: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleBack = () => {
        navigate('/admin/roles');
    };

    const filteredPermissions = allPermissions.filter(permission =>
        permission.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        permission.description.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
            </Box>
        );
    }

    if (!role) {
        return (
            <Box>
                <Typography variant="h5" color="error">
                    Rôle non trouvé
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<ArrowBackIcon />}
                    onClick={handleBack}
                    sx={{ mt: 2 }}
                >
                    Retour à la liste des rôles
                </Button>
            </Box>
        );
    }

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4" component="h1">
                    Permissions du rôle: {role.name}
                </Typography>
                <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={handleBack}
                >
                    Retour
                </Button>
            </Box>

            <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                    <Typography variant="body1" mb={2}>
                        {role.description}
                    </Typography>

                    <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" fontWeight="bold">Type:</Typography>
                        <Typography variant="body2">{role.isSystem ? 'Système' : 'Personnalisé'}</Typography>
                    </Box>

                    <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" fontWeight="bold">Statut:</Typography>
                        <Typography variant="body2">{role.isActive ? 'Actif' : 'Inactif'}</Typography>
                    </Box>
                </Paper>

                <Paper elevation={2} sx={{ p: 3 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                        <Typography variant="h5">
                            Gérer les permissions
                        </Typography>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<SaveIcon />}
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? <CircularProgress size={24} /> : 'Enregistrer les modifications'}
                        </Button>
                    </Box>

                    <TextField
                        fullWidth
                        placeholder="Rechercher une permission..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ mb: 2 }}
                    />

                    <Divider sx={{ mb: 2 }} />

                    <List>
                        {filteredPermissions.length === 0 ? (
                            <Typography variant="body1" align="center" py={2}>
                                Aucune permission trouvée
                            </Typography>
                        ) : (
                            filteredPermissions.map((permission) => (
                                <React.Fragment key={permission.id}>
                                    <ListItem
                                        button
                                        onClick={() => handleTogglePermission(permission.id)}
                                        dense
                                    >
                                        <ListItemIcon>
                                            <Checkbox
                                                edge="start"
                                                checked={selectedPermissions.includes(permission.id)}
                                                tabIndex={-1}
                                                disableRipple
                                            />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={permission.name}
                                            secondary={permission.description}
                                        />
                                    </ListItem>
                                    <Divider />
                                </React.Fragment>
                            ))
                        )}
                    </List>
                </Paper>
        </Box>
);
};

export default RolePermissions;